import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Avatar,
  Chip,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Tooltip,
  CircularProgress,
  Slide,
  InputAdornment,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Check as CheckIcon,
  ShoppingCart as CartIcon,
  ReceiptLong as ReceiptIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useEmpresa } from '../context/EmpresaContext';
import config from '../config';

const Transition = React.forwardRef((props, ref) => (
  <Slide direction="up" ref={ref} {...props} />
));

const GestionServiciosDialog = ({
  open,
  onClose,
  ordenId,
  ordenEstado,
  serviciosActuales = [],
  onServiciosUpdated,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [servicios, setServicios] = useState([]);
  const [serviciosDisponibles, setServiciosDisponibles] = useState([]);
  const [editingIndex, setEditingIndex] = useState(-1);
  const [editData, setEditData] = useState({});
  const [showAddForm, setShowAddForm] = useState(false);

  const [nuevoServicio, setNuevoServicio] = useState({
    id_servicio: '',
    cantidad: 1,
    precio: 0,
    detalle_glosa: '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const { user } = useAuth();
  const { simboloMoneda } = useEmpresa();

  const puedeModificar = [1, 2].includes(parseInt(ordenEstado));
  const moneda = simboloMoneda || 'S/';

  const calcularTotal = () => servicios.reduce((t, s) => t + (s.cantidad * s.precio), 0);

  useEffect(() => {
    if (open) {
      setServicios([...serviciosActuales]);
      fetchServiciosDisponibles();
      setError('');
      setSuccess('');
      setEditingIndex(-1);
      setShowAddForm(false);
    }
  }, [open, serviciosActuales]);

  const fetchServiciosDisponibles = async () => {
    try {
      const response = await axios.get(`${config.API_BASE_URL}/api/servicios`, {
        headers: { Authorization: `Bearer ${user.token}` },
      });
      setServiciosDisponibles(response.data);
    } catch (err) {
      setError('Error al cargar servicios');
    }
  };

  const handleServicioChange = (value) => {
    const sel = serviciosDisponibles.find(s => s.id_servicio === parseInt(value));
    setNuevoServicio(prev => ({
      ...prev,
      id_servicio: value,
      precio: sel ? parseFloat(sel.precio) : prev.precio,
    }));
  };

  const agregarServicio = async () => {
    if (!nuevoServicio.id_servicio || !nuevoServicio.cantidad || !nuevoServicio.precio) {
      setError('Complete servicio, cantidad y precio');
      return;
    }
    const sel = serviciosDisponibles.find(s => s.id_servicio === parseInt(nuevoServicio.id_servicio));
    if (!sel) { setError('Servicio no encontrado'); return; }

    setLoading(true);
    try {
      const response = await axios.post(
        `${config.API_BASE_URL}/api/detalle-ordenes`,
        {
          id_orden_servicio: ordenId,
          id_servicio: nuevoServicio.id_servicio,
          cantidad: nuevoServicio.cantidad,
          precio: nuevoServicio.precio,
          detalle_glosa: nuevoServicio.detalle_glosa,
        },
        { headers: { Authorization: `Bearer ${user.token}` } }
      );

      setServicios(prev => [...prev, {
        ...response.data,
        nombre_servicio: sel.nombre_servicio,
        descripcion_servicio: sel.descripcion_servicio,
      }]);
      setNuevoServicio({ id_servicio: '', cantidad: 1, precio: 0, detalle_glosa: '' });
      setShowAddForm(false);
      setSuccess('Servicio agregado');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al agregar');
    } finally {
      setLoading(false);
    }
  };

  const eliminarServicio = async (servicio, index) => {
    if (!window.confirm(`¿Eliminar "${servicio.nombre_servicio}"?`)) return;
    setLoading(true);
    try {
      if (servicio.id_detalle) {
        await axios.delete(
          `${config.API_BASE_URL}/api/detalle-ordenes/${servicio.id_detalle}`,
          { headers: { Authorization: `Bearer ${user.token}` } }
        );
      }
      setServicios(prev => prev.filter((_, i) => i !== index));
      setSuccess('Servicio eliminado');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al eliminar');
    } finally {
      setLoading(false);
    }
  };

  const guardarEdicion = async (index) => {
    setLoading(true);
    try {
      const servicio = servicios[index];
      if (servicio.id_detalle) {
        await axios.put(
          `${config.API_BASE_URL}/api/detalle-ordenes/${servicio.id_detalle}`,
          {
            id_servicio: servicio.id_servicio,
            cantidad: editData.cantidad,
            precio: editData.precio,
            detalle_glosa: editData.detalle_glosa,
            id_estado_servicio: servicio.id_estado_servicio || 1,
          },
          { headers: { Authorization: `Bearer ${user.token}` } }
        );
      }
      setServicios(prev => prev.map((s, i) => i === index ? { ...s, ...editData } : s));
      setEditingIndex(-1);
      setSuccess('Actualizado');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al actualizar');
    } finally {
      setLoading(false);
    }
  };

  const guardarCambios = () => {
    onServiciosUpdated && onServiciosUpdated();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth="sm"
      fullWidth
      TransitionComponent={isMobile ? Transition : undefined}
      PaperProps={{
        sx: { borderRadius: isMobile ? 0 : 3, maxHeight: isMobile ? '100%' : '90vh' },
      }}
    >
      {/* ===== HEADER ===== */}
      <Box sx={{
        background: 'linear-gradient(135deg, #0D47A1 0%, #1976D2 100%)',
        color: '#fff',
        px: 2.5, py: 2,
        display: 'flex', alignItems: 'center', gap: 1.5,
      }}>
        <Box sx={{
          width: 40, height: 40, borderRadius: 2,
          bgcolor: 'rgba(255,255,255,0.15)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <CartIcon sx={{ fontSize: 22 }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 800 }}>
            Servicios de la Orden
          </Typography>
          <Typography sx={{ fontSize: 12, opacity: 0.8 }}>
            {servicios.length} servicio{servicios.length !== 1 ? 's' : ''}
            {calcularTotal() > 0 && ` · ${moneda} ${calcularTotal().toFixed(2)} total`}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: '#fff' }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 0 }}>
        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mx: 2, mt: 2, borderRadius: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mx: 2, mt: 2, borderRadius: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}
        {!puedeModificar && (
          <Alert severity="warning" sx={{ mx: 2, mt: 2, borderRadius: 2 }}>
            Solo se pueden modificar en estado Pendiente o Asignado
          </Alert>
        )}

        {/* ===== LISTA DE SERVICIOS ===== */}
        <Box sx={{ p: 2 }}>
          {servicios.length === 0 ? (
            <Box sx={{
              textAlign: 'center', py: 4, px: 2,
              bgcolor: '#F8FAFC', borderRadius: 3, border: '2px dashed #CBD5E1',
            }}>
              <ReceiptIcon sx={{ fontSize: 48, color: '#94A3B8', mb: 1 }} />
              <Typography sx={{ color: '#64748B', fontSize: 14, fontWeight: 600 }}>
                Sin servicios agregados
              </Typography>
              <Typography sx={{ color: '#94A3B8', fontSize: 12 }}>
                Agrega el primer servicio a esta orden
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {servicios.map((s, index) => {
                const isEditing = editingIndex === index;
                const subtotal = (s.cantidad * s.precio);

                return (
                  <Box key={`${s.id_servicio}-${index}`} sx={{
                    border: '1px solid #E2E8F0',
                    borderRadius: 3,
                    bgcolor: '#fff',
                    overflow: 'hidden',
                  }}>
                    {/* Service header */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5 }}>
                      <Avatar sx={{
                        width: 40, height: 40,
                        bgcolor: '#E3F2FD', color: '#1565C0',
                        fontSize: 14, fontWeight: 800,
                      }}>
                        <CartIcon sx={{ fontSize: 20 }} />
                      </Avatar>

                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#1E293B', lineHeight: 1.2 }} noWrap>
                          {s.nombre_servicio}
                        </Typography>
                        {s.detalle_glosa && (
                          <Typography sx={{ fontSize: 11, color: '#94A3B8', mt: 0.3 }} noWrap>
                            {s.detalle_glosa}
                          </Typography>
                        )}
                      </Box>

                      {puedeModificar && !isEditing && (
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title="Editar">
                            <IconButton size="small" onClick={() => {
                              setEditingIndex(index);
                              setEditData({ cantidad: s.cantidad, precio: s.precio, detalle_glosa: s.detalle_glosa || '' });
                            }} sx={{ color: '#1A237E', width: 30, height: 30 }}>
                              <EditIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Eliminar">
                            <IconButton size="small"
                              onClick={() => eliminarServicio(s, index)}
                              sx={{ color: '#EF4444', width: 30, height: 30 }}>
                              <DeleteIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </Box>

                    {/* Price row */}
                    {!isEditing ? (
                      <Box sx={{
                        display: 'flex', borderTop: '1px solid #F1F5F9',
                        '& > div': { flex: 1, textAlign: 'center', py: 0.8 },
                      }}>
                        <Box>
                          <Typography sx={{ fontSize: 10, color: '#94A3B8', fontWeight: 600 }}>CANTIDAD</Typography>
                          <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#1E293B' }}>
                            x{s.cantidad}
                          </Typography>
                        </Box>
                        <Box sx={{ borderLeft: '1px solid #F1F5F9', borderRight: '1px solid #F1F5F9' }}>
                          <Typography sx={{ fontSize: 10, color: '#94A3B8', fontWeight: 600 }}>P. UNIT.</Typography>
                          <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#1E293B' }}>
                            {moneda} {parseFloat(s.precio || 0).toFixed(2)}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography sx={{ fontSize: 10, color: '#94A3B8', fontWeight: 600 }}>SUBTOTAL</Typography>
                          <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#1565C0' }}>
                            {moneda} {subtotal.toFixed(2)}
                          </Typography>
                        </Box>
                      </Box>
                    ) : (
                      /* Edit mode */
                      <Box sx={{ p: 1.5, pt: 0 }}>
                        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                          <TextField
                            size="small" label="Cantidad" type="number"
                            value={editData.cantidad}
                            onChange={(e) => setEditData(prev => ({ ...prev, cantidad: parseInt(e.target.value) || 1 }))}
                            inputProps={{ min: 1 }}
                            sx={{ flex: 1, '& .MuiInputBase-root': { borderRadius: 2 } }}
                          />
                          <TextField
                            size="small" label="Precio" type="number"
                            value={editData.precio}
                            onChange={(e) => setEditData(prev => ({ ...prev, precio: parseFloat(e.target.value) || 0 }))}
                            InputProps={{ startAdornment: <InputAdornment position="start">{moneda}</InputAdornment> }}
                            sx={{ flex: 1, '& .MuiInputBase-root': { borderRadius: 2 } }}
                          />
                        </Box>
                        <TextField
                          size="small" label="Observaciones" fullWidth
                          value={editData.detalle_glosa}
                          onChange={(e) => setEditData(prev => ({ ...prev, detalle_glosa: e.target.value }))}
                          sx={{ mb: 1, '& .MuiInputBase-root': { borderRadius: 2 } }}
                        />
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          <IconButton size="small" onClick={() => guardarEdicion(index)}
                            sx={{ bgcolor: '#10B981', color: '#fff', width: 34, height: 34, '&:hover': { bgcolor: '#059669' } }}>
                            <CheckIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                          <IconButton size="small" onClick={() => setEditingIndex(-1)}
                            sx={{ bgcolor: '#EF4444', color: '#fff', width: 34, height: 34, '&:hover': { bgcolor: '#DC2626' } }}>
                            <CloseIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Box>
                      </Box>
                    )}
                  </Box>
                );
              })}

              {/* Total */}
              <Box sx={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                p: 1.5, bgcolor: '#E3F2FD', borderRadius: 2.5,
              }}>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#1565C0' }}>TOTAL</Typography>
                <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#0D47A1' }}>
                  {moneda} {calcularTotal().toFixed(2)}
                </Typography>
              </Box>
            </Box>
          )}

          {/* ===== ADD FORM ===== */}
          {puedeModificar && (
            <>
              {!showAddForm ? (
                <Box
                  onClick={() => setShowAddForm(true)}
                  sx={{
                    mt: 2, p: 1.5, border: '2px dashed #CBD5E1', borderRadius: 3,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
                    cursor: 'pointer', color: '#64748B',
                    '&:hover': { borderColor: '#1565C0', color: '#1565C0', bgcolor: '#F8FAFC' },
                    transition: 'all 0.2s',
                  }}
                >
                  <AddIcon sx={{ fontSize: 20 }} />
                  <Typography sx={{ fontSize: 14, fontWeight: 600 }}>Agregar servicio</Typography>
                </Box>
              ) : (
                <Box sx={{
                  mt: 2, p: 2, borderRadius: 3,
                  bgcolor: '#EDF4FE', border: '1px solid #90CAF9',
                }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0D47A1', mb: 1.5 }}>
                    Nuevo servicio
                  </Typography>

                  {/* Service select */}
                  <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                    <InputLabel>Servicio</InputLabel>
                    <Select
                      value={nuevoServicio.id_servicio}
                      label="Servicio"
                      onChange={(e) => handleServicioChange(e.target.value)}
                      sx={{ borderRadius: 2, bgcolor: '#fff' }}
                    >
                      {serviciosDisponibles.map(s => (
                        <MenuItem key={s.id_servicio} value={s.id_servicio}>
                          <Box>
                            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                              {s.nombre_servicio}
                            </Typography>
                            <Typography sx={{ fontSize: 11, color: '#94A3B8' }}>
                              {moneda} {parseFloat(s.precio).toFixed(2)} {s.descripcion_servicio ? `- ${s.descripcion_servicio}` : ''}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Cantidad + Precio */}
                  <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                    <TextField
                      size="small" label="Cantidad" type="number" fullWidth
                      value={nuevoServicio.cantidad}
                      onChange={(e) => setNuevoServicio(prev => ({ ...prev, cantidad: parseInt(e.target.value) || 1 }))}
                      inputProps={{ min: 1 }}
                      sx={{ '& .MuiInputBase-root': { borderRadius: 2, bgcolor: '#fff' } }}
                    />
                    <TextField
                      size="small" label="Precio" type="number" fullWidth
                      value={nuevoServicio.precio}
                      onChange={(e) => setNuevoServicio(prev => ({ ...prev, precio: parseFloat(e.target.value) || 0 }))}
                      InputProps={{ startAdornment: <InputAdornment position="start">{moneda}</InputAdornment> }}
                      sx={{ '& .MuiInputBase-root': { borderRadius: 2, bgcolor: '#fff' } }}
                    />
                  </Box>

                  {/* Observaciones */}
                  <TextField
                    size="small" label="Observaciones (opcional)" fullWidth
                    value={nuevoServicio.detalle_glosa}
                    onChange={(e) => setNuevoServicio(prev => ({ ...prev, detalle_glosa: e.target.value }))}
                    sx={{ mb: 1.5, '& .MuiInputBase-root': { borderRadius: 2, bgcolor: '#fff' } }}
                  />

                  {/* Subtotal preview */}
                  {nuevoServicio.precio > 0 && (
                    <Box sx={{
                      display: 'flex', alignItems: 'center', gap: 1,
                      p: 1, borderRadius: 2, bgcolor: '#E3F2FD', mb: 1.5,
                    }}>
                      <ReceiptIcon sx={{ fontSize: 16, color: '#1565C0' }} />
                      <Typography sx={{ fontSize: 12, color: '#0D47A1', fontWeight: 600 }}>
                        Subtotal: {moneda} {(nuevoServicio.cantidad * nuevoServicio.precio).toFixed(2)}
                      </Typography>
                    </Box>
                  )}

                  {/* Action buttons */}
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Box
                      onClick={() => setShowAddForm(false)}
                      sx={{
                        flex: 1, py: 1, borderRadius: 2,
                        border: '1px solid #CBD5E1', textAlign: 'center', cursor: 'pointer',
                        fontSize: 13, fontWeight: 600, color: '#64748B',
                        '&:hover': { bgcolor: '#F1F5F9' },
                      }}
                    >
                      Cancelar
                    </Box>
                    <Box
                      onClick={loading ? undefined : agregarServicio}
                      sx={{
                        flex: 1.5, py: 1, borderRadius: 2,
                        bgcolor: !nuevoServicio.id_servicio || loading ? '#CBD5E1' : '#1565C0',
                        textAlign: 'center',
                        cursor: !nuevoServicio.id_servicio || loading ? 'default' : 'pointer',
                        fontSize: 13, fontWeight: 700, color: '#fff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5,
                        '&:hover': { bgcolor: !nuevoServicio.id_servicio || loading ? '#CBD5E1' : '#0D47A1' },
                      }}
                    >
                      {loading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <AddIcon sx={{ fontSize: 16 }} />}
                      Agregar
                    </Box>
                  </Box>
                </Box>
              )}
            </>
          )}
        </Box>
      </DialogContent>

      {/* ===== FOOTER ===== */}
      <Box sx={{
        borderTop: '1px solid #E2E8F0',
        p: 2, display: 'flex', gap: 1.5,
      }}>
        <Box
          onClick={onClose}
          sx={{
            flex: 1, py: 1.2, borderRadius: 2.5, textAlign: 'center',
            border: '1.5px solid #E2E8F0', cursor: 'pointer',
            fontSize: 14, fontWeight: 600, color: '#64748B',
            '&:hover': { bgcolor: '#F8FAFC', borderColor: '#CBD5E1' },
          }}
        >
          Cerrar
        </Box>
        <Box
          onClick={guardarCambios}
          sx={{
            flex: 1.5, py: 1.2, borderRadius: 2.5, textAlign: 'center',
            background: 'linear-gradient(135deg, #10B981, #059669)',
            cursor: 'pointer',
            fontSize: 14, fontWeight: 700, color: '#fff',
            '&:hover': { background: 'linear-gradient(135deg, #059669, #047857)' },
          }}
        >
          Guardar y cerrar
        </Box>
      </Box>
    </Dialog>
  );
};

export default GestionServiciosDialog;
